<template>
  <div>
    <h1>{{ stepState.name }}</h1>
    <div v-if="currentPage == 'address'">
      <!-- instructions and profile fields -->
      <div v-if="stepState.instructions" v-html="$sanitize(stepState.instructions)" />

      <div>
        <p>
          <i18n path="enrollmentStep.w9.checkYourAddress">
            <a href="#" data-address-change @click.prevent="showModal = true">{{ $t('enrollmentStep.w9.editYourProfile') }}</a>
          </i18n>
        </p>

        <div v-if="!addressComplete" class="alert alert-danger">
          {{ $t('enrollmentStep.w9.addYourAddress') }}
        </div>

        <address v-html="$sanitize(profileFieldsDisplay)" />
      </div>
      <b-modal
        :visible="showModal"
        :title="$t('enrollmentStep.w9.modalTitle')"
        @cancel="closeProfileForm"
      >
        <div>
          <div v-if="submitModalError" class="alert alert-danger">
            {{ $t('enrollmentStep.w9.refreshPageProfile') }}
          </div>
          <form ref="profileForm" @submit.prevent="saveProfile">
            <ProfileFields
              ref="modalProfileFields"
              v-model="modalProfileFields"
              :config="profileConfig"
            />
          </form>
        </div>
        <div slot="modal-footer" class="modal-footer">
          <button
            type="submit"
            class="btn btn-primary"
            @click="saveProfile"
          >
            {{ $t('enrollmentStep.w9.saveAddress') }}
          </button>
          <button type="button" class="btn btn-link" @click="closeProfileForm">
            {{ $t('cancel') }}
          </button>
        </div>
      </b-modal>
      <div class="form-group">
        <button
          class="btn btn-primary"
          :disabled="!addressComplete"
          data-next
          @click="changePage('social')"
        >
          {{ $t('next') }}
        </button>
      </div>
    </div>
    <div v-else-if="currentPage == 'social'">
      <div v-if="submitError" class="alert alert-danger">
        {{ $t('enrollmentStep.w9.refreshPageW9') }}
      </div>
      <h2>{{ $t('enrollmentStep.w9.ssnOrTin') }}</h2>
      <div>
        <h4>{{ profileFields.first_name }} {{ profileFields.last_name }}</h4>
        <p>{{ $t('enrollmentStep.w9.enterSsnOrTin') }}</p>
        <div class="form-inline">
          <div :class="{'has-error': errors.has('social_security_number')}" class="form-group">
            <input
              key="social_security_number-input"
              v-model="social_security_number"
              v-mask="'###-##-####'"
              v-validate="{required: true, regex: /^\d{3}-\d{2}-\d{4}$/}"
              name="social_security_number"
              class="form-control"
            >
            <ul v-if="errors.has('social_security_number')" class="error-list">
              <li>{{ errors.first('social_security_number') }}</li>
            </ul>
          </div>
        </div>
        <h3>{{ $t('enrollmentStep.w9.certification') }}</h3>

        <div class="checkbox">
          <label>
            <input v-model="certify" type="checkbox" name="certify"> {{ $t('enrollmentStep.w9.underPenalties') }}
          </label>
        </div>
        <ol>
          <li>{{ $t('enrollmentStep.w9.part1') }}</li>

          <li>{{ $t('enrollmentStep.w9.part2') }}</li>

          <li>{{ $t('enrollmentStep.w9.part3') }}</li>
        </ol>
        <p>
          {{ $t('enrollmentStep.w9.signHere') }}
        </p>
        <div class="form-group">
          <VueSignaturePad
            ref="signaturePad"
            width="100%"
            height="200px"
            :customStyle="signaturePadStyle"
            :options="{onEnd: updateSignature}"
          />
        </div>
        <div>
          <button
            class="btn btn-primary"
            data-prev
            @click="currentPage = 'address'"
          >
            {{ $t('previous') }}
          </button>
          <button
            :disabled="!w9Complete"
            class="btn btn-primary"
            data-next
            @click="saveW9()"
          >
            {{ $t('next') }}
          </button>
        </div>
      </div>
    </div>
    <div v-else>
      <!-- confirmation -->
      <div v-html="$sanitize(stepState.confirmation)" />
      <button
        class="btn btn-primary"
        data-next
        @click="$emit('refresh')"
      >
        {{ $t('continue') }}
      </button>
    </div>
  </div>
</template>

<script>
  import { mask } from 'vue-the-mask';
  import { BModal } from "bootstrap-vue";
  import VueSignaturePad from 'vue-signature-pad';
  import BaseStep from './BaseStep.vue';
  import ProfileFields from './ProfileFields.vue';

  export default {
    components: {
      ProfileFields,
      VueSignaturePad,
      BModal,
    },
    directives: {
      mask
    },
    extends: BaseStep,
    data() {
      return {
        certify: false,
        addressConfirmed: false,
        showModal: false,
        submitError: false,
        submitModalError: false,
        signaturePadStyle: { border: "black 1px solid" },
        social_security_number: null,
        signature: '',
        profileConfig: {
          "first_name": "required",
          "last_name": "required",
          "street_address": "required",
          "street_address2": "optional",
          "city": "required",
          "state_region": "required",
          "postal_code": "required"
        },
        profileModalTitle: '',
        currentPage: 'address',
        profileFields: {
          first_name: '',
          last_name: '',
          street_address: '',
          street_address2: '',
          city: '',
          state_region: '',
          postal_code: '',
        },
        modalProfileFields: {
          first_name: '',
          last_name: '',
          street_address: '',
          street_address2: '',
          city: '',
          state_region: '',
          postal_code: '',
        }
      }
    },
    computed: {
      w9Complete() {
        return this.signature && !this.signature.isEmpty && // If there is a signature and it's not empty
               this.certify && // And they have certified
               !this.$validator.errors.has('social_security_number'); // And they have entered a valid SSN
      },
      addressComplete() {
        return this.profileFields.first_name && this.profileFields.last_name && this.profileFields.street_address && this.profileFields.city && this.profileFields.state_region && this.profileFields.postal_code;
      },
      hasConfirmation() {
        return this.stepState.confirmation != undefined;
      },
      profileFieldsDisplay() {
        let pf = `<strong>${this.profileFields.first_name} ${this.profileFields.last_name}</strong>`;

        if (this.profileFields.street_address) {
          pf = `${pf}<br>${this.profileFields.street_address}`;
        }

        if (this.profileFields.street_address2) {
          pf = `${pf}<br>${this.profileFields.street_address2}`;
        }

        if (this.profileFields.city || this.profileFields.state_region || this.profileFields.postal_code) {
          pf = `${pf}<br>${this.profileFields.city}, ${this.profileFields.state_region} ${this.profileFields.postal_code}`;
        }

        return pf;
      }
    },
    methods: {
      init() {
        this.$api.Participants.getParticipant({
          participant_id: this.participantId
        }).then(({body}) => {
          let me = body.data;

          Object.keys(this.profileFields).forEach((key) => {
            if (me[key]) {
              this.profileFields[key] = me[key];
              this.modalProfileFields[key] = me[key];
            }
          });
          this.$emit('ready');
        });// TODO catch
      },
      closeProfileForm() {
        this.showModal = false;
        this.submitModalError = false; // reset alert
        Object.assign(this.modalProfileFields, this.profileFields);
      },
      changePage(page) {
        this.currentPage = page;
        this.signature = '';
      },
      updateSignature() {
        this.signature = this.$refs.signaturePad.saveSignature();
      },
      saveProfile() {
        this.$refs.modalProfileFields.validate().then((result) => {
          if (!result) {
            return;
          }

          // get list of visible form field names
          let visibleFields = Array.from(this.$refs.profileForm.elements).map((field) => field.name);

          // generate patch operation for each field that's visible
          // should this only patch fields that have changed?
          let patch = Object.keys(this.profileFields).filter((field) => {
            return visibleFields.includes(field);
          }).map((field) => {
            return { op: "replace", path: `/${field}`, value: this.modalProfileFields[field] };
          });

          return this.$api.Participants.updateParticipant({
            participant_id: this.participantId,
            patch: patch
          }).then(({status, body}) => {
            if (status >= 400) {
              throw {status: status, body: body};
            }
            Object.assign(this.profileFields, this.modalProfileFields);

            this.showModal = false;

          }).catch((error) => {
            const body = error.response.body;
            if (body.field_errors && body.field_errors.length > 0) {
              // the api returned field errors which means the ppt profile failed validation, create an incident
              let msg = ["Participant could not complete W9 step because of an error in their profile:"];
              body.field_errors.forEach((err) => {
                msg.push("Field: " + err.field);
                msg.push("Message: " + err.message);
              });
              this.$api.Participants.createIncidentForParticipant({
                participant_id: this.participantId,
                body: {
                  "type": "Software",
                  "message": msg.join("\n")
                }
              });
            }

            this.submitModalError = true;
          });
        });
      },
      saveW9() {
        this.$validator.validateAll().then((result) => {
          if (!result) {
            return;
          }

          let patch = [
            { op: "replace", path: `/social_security_number`, value: this.social_security_number },
            { op: "replace", path: `/electronic_signature`, value: this.signature.data },
          ];

          return this.$api.Participants.updateParticipant({
            participant_id: this.participantId,
            patch: patch
          }).then(({status, body}) => {
            if (status >= 400) {
              throw {status: status, body: body};
            }

            if (this.hasConfirmation) {
              this.currentPage = 'completed';
            } else {
              this.$emit('refresh');
            }
          }).catch((error) => {
            const body = error.response.body;
            if (body.field_errors && body.field_errors.length > 0) {
              // the api returned field errors which means the ppt profile failed validation, create an incident
              let msg = ["Participant could not complete W9 step because of an error in their profile:"];
              body.field_errors.forEach((err) => {
                msg.push("Field: " + err.field);
                msg.push("Message: " + err.message);
              });
              this.$api.Participants.createIncidentForParticipant({
                participant_id: this.participantId,
                body: {
                  "type": "Software",
                  "message": msg.join("\n")
                }
              });
            }

            this.submitError = true;
          });
        });
      }
    }
  }
  function safeNestedProp(object, prop) {
    let props = prop.split('.');
    return props.reduce((o, p) => (o && o[p]) ? o[p] : null, object);
  }
</script>

<style scoped>
  .no-legend {
    padding-top: 20px;
  }

  @media (max-width: 600px) {

    .checkbox label {
      width: 100%;
      padding: 10px 10px;
      margin-bottom: 5px;
      background: #eee;
      border: 1px solid #ccc;
      border-radius: 5px;
      -webkit-box-shadow: 1px 1px 5px #ccc;
      box-shadow: 1px 1px 5px #ccc;
    }

    .checkbox input[type=checkbox] {
      position: relative;
      margin-left: 0;
      margin-right: 5px;
    }
  }
</style>
